import {
  fetchData,
  fileExceedSizeInMo,
  getImageUrl,
  IframelyMedia,
  isImageFile,
  mediaFromIframely,
  uploadFileToUrl,
} from '@prismamedia/one-components';
import {
  useFocusPointGetter,
  useResolvedImageGetter,
  useUploadUrlGetter,
} from 'apollo/queries/uploadImage.image.graphql';
import { isRetryableError } from './retry';

export const embedImage = (url: string) =>
  fetchData<IframelyMedia>(
    `${config.API_ONE_IFRAMELY}/iframely?uri=${encodeURIComponent(url)}`,
  );

const validateImageFile = (file: File): void => {
  if (fileExceedSizeInMo(file, 10)) {
    throw new Error('Le fichier fait plus de 10 Mo');
  }

  if (!isImageFile(file)) {
    throw new Error("Le fichier n'est pas une image");
  }
};

export const useUploadImage = () => {
  const getUploadUrl = useUploadUrlGetter();
  const getResolvedImage = useResolvedImageGetter();
  const getFocusPoint = useFocusPointGetter();

  return async (file: File, namespace: string) => {
    try {
      validateImageFile(file);

      const {
        getUploadUrl: { key, uploadURL },
      } = await getUploadUrl({
        namespace,
        originalName: file.name,
      });

      await uploadFileToUrl(file, uploadURL);

      // Get resolved image URL with retry (S3 might have a delay before the image is accessible)
      let retryIteration = 0;

      const attemptGetResolvedImage = async (): Promise<
        Awaited<ReturnType<typeof getResolvedImage>>
      > => {
        retryIteration++;

        try {
          const result = await getResolvedImage({ key });
          if (!result?.getResolvedImage) {
            throw new Error('No resolved image data');
          }
          return result;
        } catch (error) {
          if (retryIteration >= 4) {
            throw error;
          }

          if (
            isRetryableError(error) ||
            (error instanceof Error &&
              error.message.includes('No resolved image data'))
          ) {
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * retryIteration),
            );
            return await attemptGetResolvedImage();
          }

          throw error;
        }
      };

      const resolvedImageData = await attemptGetResolvedImage();
      const {
        getResolvedImage: { url },
      } = resolvedImageData;

      const iframely = await embedImage(url);

      let oneMedia = mediaFromIframely(iframely);
      const imageUrl = getImageUrl(oneMedia);
      const imageHeight = oneMedia.iframely.meta.height;
      const imageWidth = oneMedia.iframely.meta.width;

      try {
        const res = await getFocusPoint({
          imageUrl,
          imageHeight,
          imageWidth,
        });
        const { x, y, relativeX, relativeY } = res.getFocusPoint;
        oneMedia = { ...oneMedia, focusPoint: { x, y, relativeX, relativeY } };
      } catch (e) {}

      return oneMedia;
    } catch (error) {
      throw new Error(
        `Erreur lors de l'importation du média "${file.name}": ${
          (error as Error)?.message
        }`,
      );
    }
  };
};
